import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { PhotoProvider, PhotoView } from "react-photo-view";
import EditInternalIdButton from "../../components/EditInternalIdButton";

import Utils from "../../utils";
import { getVehicleDetailsList } from "../../services/VehicleService";
import { printVehicleLabel } from "../../services/LabelServices";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { tConstStatic } from "../../utils/constantsTranslator";

const VehicleDetails = () => {
  const state = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { uuid } = useParams();
  const [vehiclePartDetails, setVehiclePartDetails] = useState("");

  useEffect(() => {
    if (state?.state?.id || uuid) {
      let data = {
        vehicle_id: state?.state?.id,
        uuid: uuid,
      };

      getVehicleDetailsList(data)
        .then((response) => {
          setVehiclePartDetails(response.data.data);
        })
        .catch((err) => {
          toast.error(Utils.getErrorMessage(err));
        });
    }
  }, [state?.state?.id, uuid]);

  const v = vehiclePartDetails?.vehicle_data || {};
  const DASH = "—";

  const fmtVal = (val) => {
    if (val === null || val === undefined || val === "") return DASH;
    return val;
  };

  const fmtDate = (val) => {
    if (!val) return DASH;
    const m = moment(val);
    return m.isValid() ? m.format("DD/MM/YYYY") : DASH;
  };

  const fmtDateTime = (val) => {
    if (!val) return DASH;
    const m = moment(val);
    return m.isValid() ? m.format("DD/MM/YYYY HH:mm") : DASH;
  };

  const steeringLabel = (val) => {
    if (val === 1) return t("left");
    if (val === 2) return t("right");
    if (val === 0) return t("not_applicable");
    return DASH;
  };

  const statusChipClass = (value) => {
    if (!value) return "vd-chip vd-chip-neutral";
    const s = String(value).toLowerCase();
    if (
      s.includes("in stock") ||
      s.includes("depolluted") ||
      s.includes("dismantled") ||
      s.includes("dismentalled") ||
      s.includes("in park") ||
      s.includes("with certificate")
    ) {
      return "vd-chip vd-chip-success";
    }
    if (
      s.includes("reserved") ||
      s.includes("in depllution") ||
      s.includes("in depollution") ||
      s.includes("in dismantling") ||
      s.includes("in dismentaling") ||
      s.includes("pending") ||
      s.includes("with declaration")
    ) {
      return "vd-chip vd-chip-warning";
    }
    if (
      s.includes("out of stock") ||
      s.includes("waste") ||
      s.includes("wastage") ||
      s.includes("cancelled")
    ) {
      return "vd-chip vd-chip-danger";
    }
    return "vd-chip vd-chip-neutral";
  };

  const Tile = ({ label, value, mono }) => (
    <div className="vd-tile">
      <div className="vd-tile-label">{label}</div>
      <div className={`vd-tile-value${mono ? " vd-mono" : ""}`}>
        {value === null || value === undefined || value === "" ? DASH : value}
      </div>
    </div>
  );

  const Chip = ({ value }) => (
    <span className={statusChipClass(value)}>
      {value ? tConstStatic(value) : DASH}
    </span>
  );

  const vehicleImages = v?.vehicle_image || [];
  const vehicleVideos = v?.vehicle_video || [];
  const vehicleDocuments = v?.document_file || [];
  const partsCheck = v?.parts_check || [];

  const brandModel = [v?.vehicle_make_name, v?.vehicle_model_name]
    .filter(Boolean)
    .join(" ");

  return (
    <div className="vd-page">
      <section className="content">
        <div className="container-fluid">
          {/* Header */}
          <div className="vd-header">
            <button
              type="button"
              className="vd-btn-back"
              onClick={() => navigate("/vehicle-process")}
              aria-label={t("back_to_vehicles")}
            >
              <i className="bi bi-arrow-left"></i>
              <span>{t("back_to_vehicles")}</span>
            </button>
            <h1 className="vd-title">{t("vehicle_details")}</h1>
            <button
              type="button"
              className="vd-btn-back"
              style={{ marginLeft: "auto" }}
              onClick={() => {
                if (!v?.id) return;
                printVehicleLabel({ vehicle_id: v.id })
                  .then((r) =>
                    toast.success(r?.data?.message || t("print_label_enqueued"))
                  )
                  .catch((err) => toast.error(Utils.getErrorMessage(err)));
              }}
            >
              <i className="bi bi-printer"></i>
              <span>{t("print_label")}</span>
            </button>
          </div>

          {/* Hero card */}
          <div className="vd-hero">
            <div className="vd-hero-main">
              <div className="vd-hero-id">
                <span className="vd-hero-id-label">{t("internal_id")}</span>
                <span
                  className="vd-hero-id-value"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  {v?.vehicle_internal_id || DASH}
                  {v?.uuid && (
                    <EditInternalIdButton
                      kind="vehicle"
                      uuid={v.uuid}
                      currentValue={v?.vehicle_internal_id || ""}
                      onUpdated={(newId) => {
                        setVehiclePartDetails({
                          ...vehiclePartDetails,
                          vehicle_data: {
                            ...vehiclePartDetails.vehicle_data,
                            vehicle_internal_id: newId,
                          },
                        });
                      }}
                    />
                  )}
                </span>
              </div>

              <div className="vd-hero-brand">
                <h2 className="vd-hero-brand-title">
                  {brandModel || t("vehicle")}
                </h2>
                <div className="vd-hero-brand-sub">
                  {v?.variants_name ? (
                    <span>{v.variants_name}</span>
                  ) : null}
                  {v?.vehicle_year ? (
                    <span className="vd-dot">•</span>
                  ) : null}
                  {v?.vehicle_year ? <span>{v.vehicle_year}</span> : null}
                </div>
              </div>
            </div>

            <div className="vd-hero-side">
              {v?.registration_plate ? (
                <div className="vd-plate" title={t("registration_plate")}>
                  <span className="vd-plate-country">
                    <span className="vd-plate-stars">★★★★★</span>
                    <span className="vd-plate-code">
                      {v?.plate_country_code || "PT"}
                    </span>
                  </span>
                  <span className="vd-plate-number">
                    {v.registration_plate}
                  </span>
                </div>
              ) : null}

              {v?.vehicle_frame ? (
                <div className="vd-vin">
                  <span className="vd-vin-label">{t("vin_number")}</span>
                  <span className="vd-vin-value">{v.vehicle_frame}</span>
                </div>
              ) : null}
            </div>
          </div>

          {/* States section */}
          <div className="vd-card">
            <div className="vd-card-header">
              <i className="bi bi-flag"></i>
              <h3>{t("section_states")}</h3>
            </div>
            <div className="vd-card-body vd-states">
              <div className="vd-state-item">
                <div className="vd-tile-label">{t("col_stock_status")}</div>
                <Chip value={v?.inventory_name} />
              </div>
              <div className="vd-state-item">
                <div className="vd-tile-label">
                  {t("col_depollution_status")}
                </div>
                <Chip value={v?.depollution_name} />
              </div>
              <div className="vd-state-item">
                <div className="vd-tile-label">
                  {t("col_dismantle_status")}
                </div>
                <Chip value={v?.dismental_name} />
              </div>
              <div className="vd-state-item">
                <div className="vd-tile-label">{t("state_parking")}</div>
                <Chip value={v?.state_parking_name} />
              </div>
            </div>
          </div>

          {/* Vehicle section */}
          <div className="vd-card">
            <div className="vd-card-header">
              <i className="bi bi-car-front"></i>
              <h3>{t("section_vehicle")}</h3>
            </div>
            <div className="vd-card-body">
              <div className="vd-grid">
                <Tile label={t("vehicle_make")} value={v?.vehicle_make_name} />
                <Tile
                  label={t("vehicle_model")}
                  value={v?.vehicle_model_name}
                />
                <Tile label={t("vehicle_year")} value={v?.vehicle_year} />
                <Tile label={t("vehicle_variant")} value={v?.variants_name} />
                <Tile label={t("vehicle_type")} value={v?.vehicle_type_name} />
                <Tile label={t("vehicle_category")} value={v?.category_name} />
                <Tile label={t("fuel_type")} value={v?.fuel_type_name} />
                <Tile label={t("vehicle_engine")} value={v?.motor_code} />
                <Tile label={t("Ccm3")} value={v?.ccm3} />
                <Tile label={t("power_Kw")} value={v?.power_kw} />
                <Tile label={t("HP")} value={v?.hp} />
                <Tile label={t("vehicle_color")} value={v?.vehicle_color} />
                <Tile label={t("vehicle_kms")} value={v?.vehicle_kms} />
                <Tile
                  label={t("cylinders")}
                  value={v?.vehicle_cyllinder}
                />
                <Tile
                  label={t("vehicle_doors")}
                  value={v?.vehicle_doors_name}
                />
                <Tile
                  label={t("steering_wheel_side")}
                  value={steeringLabel(v?.steering_wheel_side)}
                />
                <Tile label={t("brand")} value={v?.vehicle_brand} />
                <Tile
                  label={t("booklet_model")}
                  value={v?.vehicle_booklet_model}
                />
                <Tile
                  label={t("vehicle_make_etai")}
                  value={v?.vehicle_make_etai_name}
                />
                <Tile
                  label={t("vehicle_model_etai")}
                  value={v?.vehicle_model_etai_name}
                />
                <Tile
                  label={t("vehicle_variants_etai")}
                  value={v?.vehicle_variants_etai_name}
                />
                <Tile
                  label={t("vehicle_year_etai")}
                  value={v?.vehicle_year_etai}
                />
              </div>
            </div>
          </div>

          {/* Motor / Weight */}
          <div className="vd-card">
            <div className="vd-card-header">
              <i className="bi bi-speedometer2"></i>
              <h3>{t("section_motor_weight")}</h3>
            </div>
            <div className="vd-card-body">
              <div className="vd-grid">
                <Tile label={t("motorization")} value={v?.motorization} />
                <Tile label={t("tare")} value={v?.vehicle_tare} />
                <Tile
                  label={t("total_gross_weight")}
                  value={v?.vehicle_total_gross_weight}
                />
                <Tile label={t("motor_code")} value={v?.motor_code} />
                <Tile label={t("vehicle_eei")} value={v?.vehicle_eei} />
              </div>
            </div>
          </div>

          {/* References */}
          <div className="vd-card">
            <div className="vd-card-header">
              <i className="bi bi-tag"></i>
              <h3>{t("section_references")}</h3>
            </div>
            <div className="vd-card-body">
              <div className="vd-grid">
                <Tile
                  label={t("internal_ref")}
                  value={v?.vehicle_internl_ref}
                  mono
                />
                <Tile
                  label={t("external_ref")}
                  value={v?.vehicle_external_ref}
                  mono
                />
                <Tile
                  label={t("approval")}
                  value={v?.vehicle_approval}
                  mono
                />
                <Tile
                  label={t("certificate_number")}
                  value={v?.certificate_number}
                  mono
                />
                <Tile
                  label={t("provenance")}
                  value={v?.vehicle_provenance}
                />
                <Tile
                  label={t("vehicle_park")}
                  value={v?.vehicle_park_name}
                />
              </div>
            </div>
          </div>

          {/* Dates */}
          <div className="vd-card">
            <div className="vd-card-header">
              <i className="bi bi-calendar3"></i>
              <h3>{t("section_dates")}</h3>
            </div>
            <div className="vd-card-body">
              <div className="vd-grid">
                <Tile
                  label={t("process_start_date")}
                  value={fmtDate(v?.vehicle_process_start_date)}
                />
                <Tile
                  label={t("process_end_date")}
                  value={fmtDate(v?.vehicle_process_end_date)}
                />
                <Tile
                  label={t("created_at")}
                  value={fmtDateTime(v?.created_at)}
                />
                <Tile
                  label={t("last_updated_at")}
                  value={fmtDateTime(v?.updated_at)}
                />
              </div>
            </div>
          </div>

          {/* Owner */}
          {(v?.owner_name ||
            v?.owner_nif ||
            v?.owner_email ||
            v?.owner_phone ||
            v?.owner_address) && (
            <div className="vd-card">
              <div className="vd-card-header">
                <i className="bi bi-person-circle"></i>
                <h3>{t("section_owner")}</h3>
              </div>
              <div className="vd-card-body">
                <div className="vd-grid">
                  <Tile label={t("owner_name")} value={v?.owner_name} />
                  <Tile label={t("owner_nif")} value={v?.owner_nif} mono />
                  <Tile label={t("owner_email")} value={v?.owner_email} />
                  <Tile label={t("phone")} value={v?.owner_phone} />
                  <Tile label={t("owner_address")} value={v?.owner_address} />
                </div>
              </div>
            </div>
          )}

          {/* Parts check */}
          {partsCheck.length > 0 && (
            <div className="vd-card">
              <div className="vd-card-header">
                <i className="bi bi-check2-square"></i>
                <h3>{t("part_check")}</h3>
              </div>
              <div className="vd-card-body">
                <div className="table-responsive">
                  <table className="table vd-table">
                    <thead>
                      <tr>
                        <th>{t("name")}</th>
                        <th>{t("part_check_status_name")}</th>
                        <th>{t("part_converted_status_name")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {partsCheck.map((item, index) => (
                        <tr key={index}>
                          <td>{fmtVal(item?.namePartPT)}</td>
                          <td>
                            {item?.part_check_status_name ? (
                              <Chip value={item.part_check_status_name} />
                            ) : (
                              DASH
                            )}
                          </td>
                          <td>
                            {item?.part_converted_status_name ? (
                              <Chip value={item.part_converted_status_name} />
                            ) : (
                              DASH
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Images */}
          {vehicleImages.length > 0 && (
            <div className="vd-card">
              <div className="vd-card-header">
                <i className="bi bi-images"></i>
                <h3>{t("images")}</h3>
              </div>
              <div className="vd-card-body">
                <PhotoProvider>
                  <div className="vd-media-grid">
                    {vehicleImages.map((item, index) => (
                      <div className="vd-media-thumb" key={index}>
                        <PhotoView src={item?.vehicle_image}>
                          <img
                            src={item?.vehicle_image}
                            alt=""
                            loading="lazy"
                          />
                        </PhotoView>
                      </div>
                    ))}
                  </div>
                </PhotoProvider>
              </div>
            </div>
          )}

          {/* Videos */}
          {vehicleVideos.length > 0 && (
            <div className="vd-card">
              <div className="vd-card-header">
                <i className="bi bi-camera-video"></i>
                <h3>{t("vehicle_video")}</h3>
              </div>
              <div className="vd-card-body">
                <div className="vd-media-grid">
                  {vehicleVideos.map((item, index) => (
                    <div className="vd-media-thumb" key={index}>
                      <video src={item.vehicle_video} controls />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Documents */}
          {vehicleDocuments.length > 0 && (
            <div className="vd-card">
              <div className="vd-card-header">
                <i className="bi bi-file-earmark-text"></i>
                <h3>{t("vehicle_document")}</h3>
              </div>
              <div className="vd-card-body">
                <PhotoProvider>
                  <div className="vd-media-grid">
                    {vehicleDocuments.map((item, index) => (
                      <div className="vd-media-thumb" key={index}>
                        <PhotoView src={item?.document_file}>
                          <img
                            src={item?.document_file}
                            alt=""
                            loading="lazy"
                          />
                        </PhotoView>
                      </div>
                    ))}
                  </div>
                </PhotoProvider>
              </div>
            </div>
          )}

          {/* Empty state fallback */}
          {!v?.id && !v?.vehicle_id && (
            <div className="vd-empty">
              <i className="bi bi-inbox"></i>
              <p>{t("no_data_available")}</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default VehicleDetails;
